import React, { useEffect, useState } from "react";
import { connect } from "react-redux";

//ANT DESIGN IMPORTS
import { Card, Col, DatePicker, Divider, Form, Input, Row, Select, Spin, Typography } from "antd";
import { useNavigate } from "react-router-dom";

//PROJECT IMPORTS
import dayjs from "dayjs";
import "../../../assets/styles/DatePicker.css";
import { showCheckerApprovalModal } from "../../../components/CheckerModal";
import CustomButton from "../../../components/custom/CustomButton";
import MainLayout from "../../../components/layouts/MainLayout";
import TopContent from "../../../components/layouts/TopContent";
import cnt from "../../../services/definedConstant";
import { BOM } from "../../../services/definedMethod";
import { apiConfig, labelConfig, staticConfig } from "../product.config";
import TemplatePlaceholder from "./TemplatePlaceholder";

const { Title } = Typography;
const { TextArea } = Input;
const dateFormat = "YYYY-MM-DD";

const NewCertificate = (props) => {
  //DECLARED VARIABLES
  const { userData } = props;
  const { session } = userData;

  const navigate = useNavigate();
  const { newCertificateTemplateItems } = staticConfig;
  const [form] = Form.useForm();

  const [templateTypeOptions, setTemplateTypeOptions] = useState([]);
  // const [productOptions, setProductOptions] = useState([]);
  const [fixedDepositProducts, setFixedDepositProducts] = useState([]);
  const [recurringDepositProducts, setRecurringDepositProducts] = useState([]);
  const [moneyMarketProducts, setMoneyMarketProducts] = useState([]);
  const [productTypeOptions, setProductTypeOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const productType = Form.useWatch("productType", form);
  const templateType = Form.useWatch("templateType", form);
  const isDefault = Form.useWatch("isDefault", form);
  const productOptions =
    productType === 1
      ? moneyMarketProducts
      : productType === 2
        ? fixedDepositProducts
        : recurringDepositProducts;

  const disabledPastDate = (current) => {
    // Disable dates before today
    return current && current.isBefore(dayjs().startOf("day"));
  };

  const getTemplate = () => {
    setIsLoading(true);
    const url = `${apiConfig.certificateTemplates}/template`;
    let body = {
      session,
    };

    BOM.FetchReqAction(
      body,
      url,
      (err, res) => {
        if (err) {
          BOM.AlertMsg(cnt.DANGER, err);
          setIsLoading(false);
        } else {
          const objList = res;
          let tempTemplateType = [];
          let tempProducts = [];
          let tempProductType = [];
          let fixedDepositProducts = [];
          let recurringDepositProducts = [];
          let moneyMarketProducts = [];

          // Template Type Options
          objList?.templateTypeOptions?.forEach((element) => {
            tempTemplateType.push({
              value: element.value,
              label: element.text,
            });
          });

          // Product Options
          objList?.productOptions?.forEach((element) => {
            tempProducts.push({
              value: element.id,
              label: `${element.productName || element.name} (${element.productCode || element.code || ""})`,
            });
          });

          // Product Type Options
          objList?.productTypeOptions?.forEach((element) => {
            tempProductType.push({
              value: element.value,
              label: element.text,
            });
          });
          // Product Type Options
          objList?.fixedDepositProducts?.forEach((element) => {
            fixedDepositProducts.push({
              value: element.id,
              label: element.name,
            });
          });

          // Recurring Deposit Product Options
          objList?.recurringDepositProducts?.forEach((element) => {
            recurringDepositProducts.push({
              value: element.id,
              label: element.name,
            });
          });

          // Money Market Product Options
          objList?.moneyMarketProducts?.forEach((element) => {
            moneyMarketProducts.push({
              value: element.id,
              label: element.name,
            });
          });

          setTemplateTypeOptions(tempTemplateType);
          // setProductOptions(tempProducts);
          setProductTypeOptions(tempProductType);
          setFixedDepositProducts(fixedDepositProducts);
          setRecurringDepositProducts(recurringDepositProducts);
          setMoneyMarketProducts(moneyMarketProducts);
          setIsLoading(false);
        }
      },
      "treasury",
    );
  };

  useEffect(() => {
    getTemplate();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onCancel() {
    navigate("/certificate-template");
  }

  const onSubmit = (formValues) => {
    // Format dates to YYYY-MM-DD
    if (formValues.effectiveFrom) {
      formValues.effectiveFrom = dayjs(formValues.effectiveFrom).format("YYYY-MM-DD");
    }
    if (formValues.effectiveTo) {
      formValues.effectiveTo = dayjs(formValues.effectiveTo).format("YYYY-MM-DD");
    }

    // delete formValues.effectiveFrom;
    // delete formValues.effectiveTo;
    formValues.locale = "en";
    formValues.dateFormat = "yyyy-MM-dd";

    const body = {
      ...formValues,
      method: "POST",
      session,
    };

    setIsLoading(true);
    BOM.FetchReqAction(
      body,
      apiConfig.certificateTemplates,
      (err, res) => {
        if (err) {
          BOM.AlertMsg(cnt.DANGER, err?.errors[0]?.defaultUserMessage || err);
          setIsLoading(false);
        } else {
          BOM.AlertMsg(cnt.SUCCESS, "Certificate template created successfully");
          navigate("/certificate-template");
          setIsLoading(false);
        }
      },
      {
        navigate,
        showCheckerModal: showCheckerApprovalModal,
        onCheckerApproval: (res) => {
          navigate("/certificate-template");
          setIsLoading(false);
        },
      },
      "treasury",
    );
  };

  const onChangeTemplateType = () => {
    form.setFieldValue("productId", null);
  };

  useEffect(() => {
    const isTreasury = templateType === 1 || templateType === 2;
    const isFixedDeposit = templateType === 3;

    isTreasury && form.setFieldValue("productType", 1);
    isFixedDeposit && form.setFieldValue("productType", 2);
  }, [templateType]);

  return (
    <MainLayout isEnableRightSidebar={false} onSave={onSubmit}>
      <TopContent
        pageTitle={labelConfig.newCertificateTemplate}
        pageItems={newCertificateTemplateItems}
      />
      <Spin spinning={isLoading} size="large">
        <Card
          headStyle={{ borderBottomWidth: 0 }}
          style={{
            boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
            borderRadius: 20,
          }}
        >
          <Form
            initialValues={{
              isDefault: false,
            }}
            form={form}
            layout="vertical"
            onFinish={onSubmit}
          >
            <Row>
              <Title level={4} style={{ margin: 0, marginBottom: 0 }}>
                Certificate Template Setup
              </Title>
            </Row>
            <Divider style={{ marginTop: 10, marginBottom: 30 }} />
            <Row>
              <Col span={8} style={{ padding: "0px 8px" }}>
                <Form.Item
                  label={labelConfig.templateName}
                  name="templateName"
                  rules={[
                    {
                      required: true,
                      message: BOM.CheckReqFld(labelConfig.templateName),
                    },
                  ]}
                >
                  <Input placeholder={BOM.handlePlaceholderMsg(labelConfig.templateName)} />
                </Form.Item>
              </Col>
              <Col span={8} style={{ padding: "0px 8px" }}>
                <Form.Item
                  label={labelConfig.templateCode}
                  name="templateCode"
                  rules={[
                    {
                      required: true,
                      message: BOM.CheckReqFld(labelConfig.templateCode),
                    },
                  ]}
                >
                  <Input placeholder={BOM.handlePlaceholderMsg(labelConfig.templateCode)} />
                </Form.Item>
              </Col>
              <Col span={8} style={{ padding: "0px 8px" }}>
                <Form.Item
                  label={labelConfig.templateType}
                  name="templateType"
                  rules={[
                    {
                      required: true,
                      message: BOM.CheckReqFld(labelConfig.templateType),
                    },
                  ]}
                >
                  <Select
                    placeholder={BOM.handlePlaceholderMsg(
                      `${labelConfig.templateType} *`,
                      "select",
                    )}
                    options={templateTypeOptions}
                    showSearch
                    onChange={onChangeTemplateType}
                  />
                </Form.Item>
              </Col>
              <Col span={24} style={{ padding: "0px 8px" }}>
                <Form.Item label={labelConfig.description} name="description">
                  <Input placeholder={BOM.handlePlaceholderMsg(labelConfig.description)} />
                </Form.Item>
              </Col>

              <Col span={24} style={{ padding: "0px 8px" }}>
                <Form.Item
                  label={labelConfig.htmlContent}
                  name="htmlContent"
                  rules={[
                    {
                      required: true,
                      message: BOM.CheckReqFld(labelConfig.htmlContent),
                    },
                  ]}
                >
                  <TextArea
                    rows={10}
                    placeholder={BOM.handlePlaceholderMsg(labelConfig.htmlContent)}
                  />
                </Form.Item>
              </Col>

              <Col span={8} style={{ padding: "0px 8px" }}>
                <Form.Item
                  label={labelConfig.productType}
                  name="productType"
                  rules={[
                    {
                      required: true,
                      message: BOM.CheckReqFld(labelConfig.productType),
                    },
                  ]}
                >
                  <Select
                    disabled
                    placeholder={BOM.handlePlaceholderMsg(`${labelConfig.productType} *`, "select")}
                    options={productTypeOptions}
                    showSearch
                  />
                </Form.Item>
              </Col>
              <Col span={8} style={{ padding: "0px 8px" }}>
                <Form.Item label={labelConfig.isDefault} name="isDefault">
                  <Select
                    placeholder={BOM.handlePlaceholderMsg(labelConfig.isDefault, "select")}
                    options={staticConfig.templateStatusData}
                  />
                </Form.Item>
              </Col>
              {!isDefault && (
                <Col span={8} style={{ padding: "0px 8px" }}>
                  <Form.Item
                    label={labelConfig.productId}
                    name="productId"
                    rules={[
                      {
                        required: true,
                        message: BOM.CheckReqFld(labelConfig.productId),
                      },
                    ]}
                  >
                    <Select
                      disabled={!productType}
                      placeholder={BOM.handlePlaceholderMsg(`${labelConfig.productId} *`, "select")}
                      options={productOptions}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label?.toLowerCase() ?? "").includes(input?.toLowerCase())
                      }
                    />
                  </Form.Item>
                </Col>
              )}
              <Col span={8} style={{ padding: "0px 8px" }}>
                <Form.Item
                  label={labelConfig.effectiveFrom}
                  name="effectiveFrom"
                  rules={[
                    {
                      required: true,
                      message: BOM.CheckReqFld(labelConfig.effectiveFrom),
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const effectiveTo = getFieldValue("effectiveTo");
                        if (!value || !effectiveTo) {
                          return Promise.resolve();
                        }
                        if (value.isAfter(effectiveTo, "day") || value.isSame(effectiveTo, "day")) {
                          return Promise.reject(
                            new Error("Effective From date must be before Effective To date"),
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                  validateTrigger={["onChange", "onBlur"]}
                >
                  <DatePicker
                    disabledDate={(current) => {
                      const effectiveTo = form.getFieldValue("effectiveTo");
                      if (effectiveTo) {
                        return (
                          current &&
                          (current.isBefore(dayjs().startOf("day")) ||
                            current.isAfter(effectiveTo, "day") ||
                            current.isSame(effectiveTo, "day"))
                        );
                      }
                      return disabledPastDate(current);
                    }}
                    style={{ width: "100%", height: 32.5 }}
                    format={dateFormat}
                    placeholder={BOM.handlePlaceholderMsg(labelConfig.effectiveFrom, "select")}
                  />
                </Form.Item>
              </Col>
              <Col span={8} style={{ padding: "0px 8px" }}>
                <Form.Item
                  label={labelConfig.effectiveTo}
                  name="effectiveTo"
                  rules={[
                    {
                      required: true,
                      message: BOM.CheckReqFld(labelConfig.effectiveTo),
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const effectiveFrom = getFieldValue("effectiveFrom");
                        if (!value || !effectiveFrom) {
                          return Promise.resolve();
                        }
                        if (
                          value.isBefore(effectiveFrom, "day") ||
                          value.isSame(effectiveFrom, "day")
                        ) {
                          return Promise.reject(
                            new Error("Effective To date must be after Effective From date"),
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                  validateTrigger={["onChange", "onBlur"]}
                >
                  <DatePicker
                    disabledDate={(current) => {
                      const effectiveFrom = form.getFieldValue("effectiveFrom");
                      if (effectiveFrom) {
                        return (
                          current &&
                          (current.isBefore(dayjs().startOf("day")) ||
                            current.isBefore(effectiveFrom, "day") ||
                            current.isSame(effectiveFrom, "day"))
                        );
                      }
                      return disabledPastDate(current);
                    }}
                    style={{ width: "100%", height: 32.5 }}
                    format={dateFormat}
                    placeholder={BOM.handlePlaceholderMsg(labelConfig.effectiveTo, "select")}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Col span={24} style={{ padding: "0px 8px" }}>
              <TemplatePlaceholder templateType={templateType} />
            </Col>
            <Row>
              <Divider />
              {BOM.HasPermission("CREATE_CERTIFICATE_TEMPLATE") && (
                <CustomButton
                  content="Submit"
                  style={{
                    width: "10%",
                    marginLeft: "auto",
                    boxShadow: "0px 0px 10px 5px rgba(0,0,0,0.1)",
                  }}
                  htmlType="submit"
                  backgroundColor="#15CCAE"
                  colorText="black"
                  hoverBackground="#4FECA0"
                />
              )}
              <CustomButton
                content="Cancel"
                style={{
                  width: "10%",
                  marginLeft: "1%",
                  boxShadow: "0px 0px 10px 5px rgba(0,0,0,0.1)",
                }}
                backgroundColor="#fff"
                colorText="black"
                hoverBackground="#F5F5F5"
                onClick={onCancel}
              />
            </Row>
          </Form>
        </Card>
      </Spin>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
  rightBtn: state.RightSideBtn,
});

export default connect(mapStateToProps, null)(NewCertificate);
